/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CpeField, FilterOperators } from "data/@types";

import { CompanyAssetGroupFilterDescModel, CompanyAssetGroupFilterModel, CompanyAssetGroupModel } from "data/models/CompanyModel";
import { KeyValue } from "lib/KeyValue";

export interface SelectedAssetFiltersState {

   modified: boolean;

   filtersLoading: boolean;
   filterLoading: boolean;

   filterDescsLoading: KeyValue<boolean>; // key = filterId
   filterDescLoading: KeyValue<boolean>; // key = filterId + filterDescId

   selectedFilter?: CompanyAssetGroupFilterModel;
   filters: CompanyAssetGroupFilterModel[];

   selectedFilterDesc?: CompanyAssetGroupFilterDescModel;
   filterDescs: KeyValue<CompanyAssetGroupFilterDescModel[]>; // key = filterId

   assetFilterCreating: boolean;
   assetFilterDeleting: boolean;

   assetFilterDescCreating: KeyValue<boolean>; // key = filterId
   assetFilterDescDeleting: KeyValue<boolean>; // key = filterId + filterDescId

}

const initialState: SelectedAssetFiltersState = {

   modified: false,

   filtersLoading: false,
   filters: [],

   filterLoading: false,

   filterDescsLoading: {},
   filterDescLoading: {},
   filterDescs: {},

   assetFilterCreating: false,
   assetFilterDeleting: false,

   assetFilterDescCreating: {},
   assetFilterDescDeleting: {}

}

export const slice = createSlice({

   name: "selectedAssetFilters",

   initialState,

   reducers: {

      saveSelectedAssetFilters: (state, action: PayloadAction<{ assetGroup: CompanyAssetGroupModel }>) => {
         state.filtersLoading = true;
      },

      saveSelectedAssetFiltersAs: (state, action: PayloadAction<{ companyId: string, assetGroupOrName: CompanyAssetGroupModel | string, assetGroupDescription: string, merge: boolean }>) => {
         state.filtersLoading = true;
      },

      selectedAssetFilterSaved: (state) => {
         state.filtersLoading = false;
         state.modified = false;
      },

      selectedAssetFilterSaveError: (state, action: PayloadAction<{ err: any }>) => {
         state.filtersLoading = false;
         console.error(action.payload.err);
      },

      setAssetFiltersLoading: (state, action: PayloadAction<{ filtersLoading: boolean }>) => {
         state.filtersLoading = action.payload.filtersLoading;
         if (action.payload.filtersLoading) state.selectedFilter = undefined;
      },

      loadAssetFilters: (state, action: PayloadAction<{ companyId: string }>) => {
         state.filtersLoading = true;
         state.selectedFilter = undefined;
      },

      assetFiltersLoaded: (state, action: PayloadAction<{ filters: CompanyAssetGroupFilterModel[] }>) => {
         state.filtersLoading = false;
         state.filters = action.payload.filters;
         state.filterDescs = {};

         action.payload.filters.forEach(
            filter => state.filterDescs[filter.id] = []
         )
         state.modified = false;
      },

      filtersLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.filtersLoading = false;
         console.error(action.payload.err);
      },

      selectAssetFilter: (state, action: PayloadAction<{ filterId: string }>) => {
         state.selectedFilter = state.filters.find(f => f.id === action.payload.filterId);
      },

      loadAssetFilter: (state, action: PayloadAction<{ companyId: string, filterId: string }>) => {
         state.filterLoading = true;
      },

      assetFilterLoaded: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel }>) => {
         state.filterLoading = false;

         const index = state.filters.findIndex(f => f.id === action.payload.filter.id);

         if (index >= 0) {
            state.filters[index] = action.payload.filter;
         } else {
            state.filters.push(action.payload.filter);
         }

         state.filterDescs[action.payload.filter.id] = [];
         state.selectedFilter = action.payload.filter;
      },

      assetFilterLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.filterLoading = false;
         console.error(action.payload.err);
      },

      loadAssetFilterDescs: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel }>) => {
         state.filterDescsLoading[action.payload.filter.id] = true;
      },

      assetFilterDescsLoaded: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel, filterDescs: CompanyAssetGroupFilterDescModel[] }>) => {
         delete state.filterDescsLoading[action.payload.filter.id];
         state.filterDescs[action.payload.filter.id] = action.payload.filterDescs;
      },

      assetFilterDescsLoadingError: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel, err: any }>) => {
         delete state.filterDescsLoading[action.payload.filter.id];
         console.error(action.payload.err);
      },

      selectAssetFilterDesc: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel, filterDescId: string }>) => {
         state.selectedFilterDesc = state.filterDescs[action.payload.filter.id]?.find(fd => fd.id ===  action.payload.filterDescId);;
      },

      loadAssetFilterDesc: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel, descId: string }>) => {
         state.filterDescLoading[action.payload.filter.id + action.payload.descId] = true;
      },

      assetFilterDescLoaded: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel, filterDesc: CompanyAssetGroupFilterDescModel }>) => {

         delete state.filterDescLoading[action.payload.filter.id + action.payload.filterDesc.id];

         const index = state.filterDescs[action.payload.filter.id]?.findIndex(fd => fd.id ===  action.payload.filterDesc.id);

         if (index >= 0) {
            state.filterDescs[action.payload.filter.id][index] = action.payload.filterDesc
         } else {

            if (!state.filterDescs[action.payload.filter.id]) {
               state.filterDescs[action.payload.filter.id] = [ action.payload.filterDesc ];
            } else {
               state.filterDescs[action.payload.filter.id].push(action.payload.filterDesc);
            }

         }

         state.selectedFilterDesc = action.payload.filterDesc;

      },

      assetFlterDescLoadingError: (state, action: PayloadAction<{ f_fd_id: string, err: any }>) => {
         delete state.filterDescLoading[action.payload.f_fd_id];
         console.error(action.payload.err);
      },

      createAssetFilter: (state, action: PayloadAction<{ companyId: string, vendor: string, product: string }>) => {
         state.assetFilterCreating = true;
      },

      assetFilterCreated: (state, action: PayloadAction<{ filter: CompanyAssetGroupFilterModel }>) => {
         state.assetFilterCreating = false;
         state.filters.push(action.payload.filter);
         state.filterDescs[action.payload.filter.id] = [];
         state.modified = true;
      },

      assetFilterCreationError: (state, action: PayloadAction<{ err: any }>) => {
         state.assetFilterCreating = false;
         console.error(action.payload.err);
      },

      createAssetFilterDesc: (
         state,
         action: PayloadAction<{
            companyId: string,
            filterId: string,
            field: CpeField,
            operator: FilterOperators,
            value: string,
            negate: boolean
         }>
      ) => {
         state.assetFilterDescCreating[action.payload.filterId] = true;
      },

      assetFilterDescCreated: (state, action: PayloadAction<{ assetFilterDesc: CompanyAssetGroupFilterDescModel }>) => {
         delete state.assetFilterDescCreating[action.payload.assetFilterDesc.filterId];
         state.filterDescs[action.payload.assetFilterDesc.filterId].push(action.payload.assetFilterDesc);
         state.modified = true;
      },

      assetFilterDescCreationError: (state, action: PayloadAction<{ filterId: string, err: any }>) => {
         delete state.assetFilterDescCreating[action.payload.filterId];
         console.error(action.payload.err);
      },

      deleteAssetFilterById: (state, action: PayloadAction<{ filterId: string }>) => {
         state.assetFilterDeleting = true;
      },

      assetFilterDeleted: (state, action: PayloadAction<{ id: string }>) => {
         state.assetFilterDeleting = false;
         delete state.filterDescs[action.payload.id];
         const index = state.filters.findIndex(f => f.id === action.payload.id);
         state.filters.splice(index, 1);
         state.modified = true;
      },

      assetFilterDeletionError: (state, action: PayloadAction<{ id: string, err: any }>) => {
         state.assetFilterDeleting = false;
         console.error(action.payload.err);
      },

      deleteAssetFilterDescById: (state, action: PayloadAction<{ filterId: string, id: string }>) => {
         state.assetFilterDescDeleting[action.payload.filterId + action.payload.id] = true;
      },

      assetFilterDescDeleted: (state, action: PayloadAction<{ filterId: string, id: string }>) => {
         delete state.assetFilterDescDeleting[action.payload.filterId + action.payload.id];
         const index = state.filterDescs[action.payload.filterId].findIndex(fd => fd.id === action.payload.id);
         state.filterDescs[action.payload.filterId].splice(index, 1);
         state.modified = true;
      },

      assetFilterDescDeletionError: (state, action: PayloadAction<{ filterId: string, id: string, err: any }>) => {
         delete state.assetFilterDescDeleting[action.payload.filterId + action.payload.id];
         console.error(action.payload.err);
      },

      createAssetFilterAndDesc: (state, action: PayloadAction<{
         companyId: string,
         vendor: string,
         product: string,
         field: CpeField,
         operator: FilterOperators,
         value: string,
         negate: boolean
      }>) => {
         // states are set in reducers called from the epics (createAssetFilterAndDesc_FilterStage, )
      },

      // just helper for createAssetFilterAndDesc
      createAssetFilterAndDesc_FilterStage: (state) => {
         state.assetFilterCreating = true;
      }

   }

});

export const actions = slice.actions;

export default slice.reducer;